<template>
    <Tooltip class="min-w-full" :viewport-overpassing-reference="true" :is-teleported="true">
        <template #trigger="tooltipProps">
            <button
                type="button"
                class="w-full outline-hidden focus-visible:ring-inset focus-visible:ring-offset-0 focus-visible:ring-2 focus-visible:ring-gray-700 cursor-pointer aria-disabled:cursor-not-allowed"
                :class="[backgroundClass, borderVerticalClasses]"
                :style="minHeightStyle"
                :aria-expanded="props.isExpanded"
                :aria-disabled="isInert"
                :aria-labelledby="labelId"
                :aria-describedby="tooltipProps['aria-labelledby']"
                :popovertarget="tooltipProps['popovertarget']"
                @mouseover="handleRowMouseOver"
                @mousedown="handleRowMouseDown"
                @mouseup="setInactive"
                @mouseleave="setInactive"
                @click="handleToggleExpanded"
            >
                <span :id="labelId" class="sr-only">
                    {{ props.label }}
                </span>
            </button>
        </template>

        <template #text>
            <template v-if="controlExplanation">
                {{ controlExplanation }}
            </template>

            <template v-else>
                {{ props.isExpanded ? $t('collapse-timeline-section') : $t('expand-timeline-section') }}
            </template>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed, inject, useId } from 'vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { useActiveStyleSync } from '../composables/useActiveStyleSync.ts';
import { useHeightSync } from '../composables/useHeightSync.ts';
import type { SectionExpandableState } from '../composables/useSectionRows.ts';
import { hoveredTimelinePartsKey, hoverTimelinePartKey } from '../injectionKeys.ts';

type SectionDaysRowStatus = 'Has-Unresolved-Conflicts' | 'No-Conflicts';

type SectionDaysRowProps = {
    /** Shared row identity: pairs this row with its first-column cell for hover and height sync. */
    rowId: string;
    label: string;
    isExpanded: boolean;
    /**
     * The state of the expand/collapse control (mirrors the first-column cell). The row toggles only
     * when `Enabled`; `Disabled`/`Empty` make it inert and supply the tooltip text.
     */
    expandableState: SectionExpandableState;
    status: SectionDaysRowStatus;
};

const props = defineProps<SectionDaysRowProps>();

const emit = defineEmits<{
    (e: 'toggle-section-expanded'): void;
}>();

// The row is non-interactive unless the section is expandable (enabled): locked (holds the selection
// source) or empty sections don't toggle.
const isInert = computed(() => props.expandableState.type !== 'Enabled');
// Tooltip text for the disabled/empty states; undefined when enabled (falls back to collapse/expand).
const controlExplanation = computed(() =>
    props.expandableState.type === 'Enabled' ? undefined : props.expandableState.explanation,
);

const labelId = useId();

const hoverTimelinePart = inject(hoverTimelinePartKey);
if (!hoverTimelinePart) {
    throw new Error('SectionDaysRow must be used within a Timeline component');
}

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
if (!hoveredTimelineParts) {
    throw new Error('SectionDaysRow must be used within a Timeline component');
}

const isHovered = computed(() => hoveredTimelineParts.rowId.value === props.rowId);

const { heightInRem } = useHeightSync(props.rowId, 1.5);
const minHeightStyle = computed(() => {
    return {
        minHeight: `${heightInRem.value}rem`,
    };
});

const { isActive, setActive, setInactive } = useActiveStyleSync(props.rowId);

const hasUnresolvedConflicts = computed(() => props.status === 'Has-Unresolved-Conflicts');

const backgroundClass = computed(() => {
    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return isActive.value ? 'bg-red-800' : 'bg-red-700';
        }

        return isActive.value ? 'bg-gray-800' : 'bg-gray-700';
    }

    if (hasUnresolvedConflicts.value) {
        return isActive.value ? 'bg-red-200' : 'bg-red-100';
    }

    return isActive.value ? 'bg-gray-300' : 'bg-gray-200';
});

const borderVerticalClasses = computed(() => {
    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return 'section-days-row-border-vertical section-days-row-border-vertical-red-700 section-days-row-border-vertical-z-20';
        }

        return 'section-days-row-border-vertical section-days-row-border-vertical-gray-600 section-days-row-border-vertical-z-20';
    }

    if (hasUnresolvedConflicts.value) {
        return 'section-days-row-border-vertical section-days-row-border-vertical-red-200 section-days-row-border-vertical-z-20';
    }

    return 'section-days-row-border-vertical section-days-row-border-vertical-gray-300 section-days-row-border-vertical-z-10';
});

const handleToggleExpanded = () => {
    if (isInert.value) {
        return;
    }
    emit('toggle-section-expanded');
};

// A locked or empty section is not interactive, so it must not highlight itself. But the mouse IS over
// it now, so clear any previous row's hover (hover nothing) rather than leaving a stale highlight behind.
const handleRowMouseOver = () => {
    hoverTimelinePart(isInert.value ? null : props.rowId, null, false);
};

const handleRowMouseDown = () => {
    if (isInert.value) {
        return;
    }
    setActive();
};
</script>

<style scoped>
.section-days-row-border-vertical {
    position: relative;
}

.section-days-row-border-vertical::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.section-days-row-border-vertical::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.section-days-row-border-vertical-z-10::before,
.section-days-row-border-vertical-z-10::after {
    z-index: 10;
}

.section-days-row-border-vertical-z-20::before,
.section-days-row-border-vertical-z-20::after {
    z-index: 20;
}

.section-days-row-border-vertical-gray-300::before,
.section-days-row-border-vertical-gray-300::after {
    background-color: var(--color-gray-300);
}

.section-days-row-border-vertical-gray-600::before,
.section-days-row-border-vertical-gray-600::after {
    background-color: var(--color-gray-600);
}

.section-days-row-border-vertical-red-200::before,
.section-days-row-border-vertical-red-200::after {
    background-color: var(--color-red-200);
}

.section-days-row-border-vertical-red-700::before,
.section-days-row-border-vertical-red-700::after {
    background-color: var(--color-red-700);
}
</style>
